'use client'
import { useState, useEffect } from 'react'
import type { ChangeEvent, JSX } from 'react'
import type { UseFormRegister as TypeRegister, FieldError } from 'react-hook-form'
import styled from 'styled-components'
import { noRegister } from './Input'
import { mq, presets, type Col } from '@/styles'
import { MODALS } from '@/context'
import { useModal } from '@/hooks'
import { TypeColors } from '@/schema'


type onChangeType = (event: ChangeEvent<HTMLInputElement>) => void

interface iCheckbox {
  readonly name: string
  readonly label: string
  defaultChecked?: boolean
  readonly required?: boolean
  readonly disabled?: boolean
  readonly readOnly?: boolean
  readonly onChange?: onChangeType
  readonly col?: Col
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly register?: TypeRegister<any>
  error?: FieldError | { message: string, [key: string]: unknown }
  readonly textColor?: TypeColors
  readonly bgColor?: TypeColors
  readonly switchAppearance?: boolean
  modalId?: MODALS
  readonly customTickBox?: boolean
  id?: string
}

interface iLabelProps {
  $col: Col
  $textColor?: TypeColors
  $bgColor: TypeColors
  $readOnly?: boolean
  $checked: boolean
}

export const Checkbox = ({ name, label, defaultChecked = undefined, required = false, disabled = false, readOnly = null, onChange = null, col = 12, register = noRegister, textColor = '--color-black', bgColor = '--color-white', switchAppearance = false, error, modalId, id='' }: iCheckbox): JSX.Element => {
  const requireProps = typeof required === 'boolean' ? { required } : required
  const { onChange:registerOnChange, name:registerName, ref:registerRef } = register(name, { ...requireProps })
  const [checked, setChecked] = useState(Boolean(defaultChecked))
  const { isActive, payload } = useModal(modalId)

  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
    setChecked(e.target.checked)
  }

  useEffect(() => {
    if(typeof defaultChecked === 'boolean') setChecked(defaultChecked)
  }, [defaultChecked])

  useEffect(() => {
    // This useEffect will only run if the <Checkbox /> is inside a modal
    if (!modalId || !isActive) return
    setChecked(Boolean(payload?.[name]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, modalId])

  return (
    <Label $col={col} $bgColor={bgColor} $textColor={textColor} $checked={checked} $readOnly={readOnly} >
      <input type='checkbox' id={id} {...registerRef && { ref: registerRef }} name={registerName ?? name} required={required} readOnly={readOnly} disabled={disabled} onChange={handleChange} {...typeof defaultChecked === 'boolean' && { defaultChecked }}
      />
      {switchAppearance ? <Switch $checked={checked} /> :<TickBox $checked={checked} $error={!!error} />}
      <span>{label}</span>
    </Label>
  )
}

const Label = styled.label<iLabelProps>`
  align-items: flex-start;
  color: var(--color-black);
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
  padding: 4.25px 4.25px 0;
  position: relative;
  pointer-events: ${({ $readOnly }) => $readOnly ? 'none' : 'auto'};
  width: auto;

  ${mq.greaterThan('tablet') } {
    margin-bottom: 30px;
    padding: 12.5px 12.5px 0;
    width: ${({ $col }) => typeof $col === 'number' ? `${100 * $col / 12}%` : $col};
  }


  > div {
    border-color: var(--color-text);
  }

  input {
    opacity: 0;
    pointer-events: ${({ $readOnly }) => $readOnly ? 'none' : 'auto'};
    position: absolute;
    user-select: none;
    visibility: hidden;
  }

  &:hover {
    input + div {
      opacity: .8;
    }
  }

  span {
    ${presets.font15}
    display: inline-block;
    margin-top: 3px;
    user-select: none;
  }
`

const TickBox = styled.div<{ $checked: boolean, $error?: boolean }>`
  background-color: ${({ $checked }) => $checked ? 'var(--color-text)' : 'transparent'};
  border: 1px solid;
  border-radius: 8px;
  flex-shrink: 0;
  height: 16px;
  margin-right:10px;
  margin-top: 2px;
  position: relative;
  transition: 250ms background-color linear, 250ms opacity linear;
  user-select: none;
  width: 16px;
`

const Switch = styled.div<{ $checked: boolean, $error?: boolean }>`
  align-items: center;
  background: ${({ $checked }) => $checked ? 'var(--color-text)' : 'var(--colo-black)'};
  border-radius: 9.5px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 17px;
  margin-right:20px;
  margin-top: 5px;
  padding: 0 1px;
  position: relative;
  transition: 250ms background-color linear, 250ms opacity linear;
  width: 30px;

  &:after {
    background: var(--color-white);
    border-radius: 50%;
    content: '';
    height: 15px;
    margin-left: ${({ $checked }) => $checked ? '15px' : 0};
    transition: 250ms margin-left linear;
    width: 15px;
  }
`

